import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import useLang from "../../../hooks/useLang";
import { useParams } from "react-router-dom";
import useSearchParams from "../../../hooks/useSearchParams";
import useConstants from "./useConstants";
import useFormula from "../formula/hooks/useFomula";
import useHotspots from "../hotspots/hooks/useHotspots";
import useVariationsFilter from "./useVariationsFilter";
import getVariationIdFromCombo from "../utils/getVariationIdFromCombo";
import useVariations from "./useVariations";
import usePolicyChecker from "../../auth/usePolicyChecker";
import { userActions } from "../../../config/accessConfig";
import { selectConfigurations } from "../configurationsSlice";

const SEED_PARAMETERS = {
  "111-111": {
    WIDTH: { values: [90, 100, 110], defaultValue: 100 },
    COLOR: {
      values: [
        "Šviesus korpusas/Šviesus stalčius",
        "Rudas korpusas/Rudas stalčius",
        "Tamsus korpusas/Šviesus stalčius",
        "Tamsus korpusas/Tamsus stalčius",
      ],
      defaultValue: "Tamsus korpusas/Tamsus stalčius",
    },
  },
  "222-222": {
    WIDTH: { values: [180, 210, 250], defaultValue: 210 },
    COLOR: {
      values: ["Šviesus korpusas", "Rudas korpusas", "Tamsus korpusas"],
      defaultValue: "Šviesus korpusas",
    },
  },
};

const PARAM_LABEL_TO_ID = { Plotis: "WIDTH", Spalva: "COLOR" };

const useConfigurationPage = () => {
  const lang = useLang();
  const disableChange = !usePolicyChecker({
    action: userActions.SAVE_CONFIGURATION,
  });
  const configurations = useSelector(selectConfigurations);
  const { configurationId } = useParams();
  const config = configurations.find((c) => c.id === configurationId);

  const baseParameters = useMemo(() => {
    const parameterLabels = config?.parameters || [];
    const seed = SEED_PARAMETERS[configurationId] || {};
    return parameterLabels.map((label) => {
      const id = PARAM_LABEL_TO_ID[label];
      const seedData = seed[id];
      return {
        id,
        label: id === "WIDTH" ? lang("width") : lang("color"),
        values: seedData?.values || [],
        defaultValue: seedData?.defaultValue ?? null,
      };
    });
  }, [configurationId, config?.parameters, lang]);

  const [parameters, setParameters] = useState({ [configurationId]: baseParameters });

  useEffect(() => {
    setParameters((prev) => ({
      ...prev,
      [configurationId]: baseParameters,
    }));
  }, [configurationId, baseParameters]);
  const defaultVariation = getVariationIdFromCombo(
    configurationId,
    Array.isArray(parameters[configurationId])
      ? parameters[configurationId].map((parameter) => parameter.defaultValue)
      : [],
  );
  const [defaultVariationId, setDefaultVariationId] =
    useState(defaultVariation);
  const { getParam } = useSearchParams();
  const variationFromParam = getParam("var");
  const { constantsArray, onConstantsChange } = useConstants(configurationId);
  const { onFilterChange, getFilteredVariations } = useVariationsFilter();
  const { hotspots, setInitialHotspots, moveHotspots, removeHotspotById } =
    useHotspots(configurationId);
  const [showParametersDialog, setShowParametersDialog] = useState(false);

  const { tokens, onFormulaChange, getPrice, errorInFormula, history: formulaHistory, restoreFormula } = useFormula({
    constants: constantsArray,
    parameters: parameters[configurationId] || [],
    configurationId,
  });

  const activeVariationId = variationFromParam || defaultVariationId;

  const { variations, togglePrice } = useVariations({
    parameters,
    configurationId,
    activeVariationId,
  });

  const activeVariation = variations?.filter(
    (variation) => variation?.active,
  )[0];

  const filteredVariations = getFilteredVariations(variations);

  const onHotspotDrag = (data) => {
    moveHotspots({ [activeVariationId]: data });
  };

  const onDuplicateHotspotCoords = () => {
    const visibleIds = filteredVariations.map((v) => v.id); // adjust if the field isn't `id`
    const active = hotspots?.[activeVariationId] ?? [];

    const payload = Object.fromEntries(visibleIds.map((id) => [id, active]));

    moveHotspots(payload);
  };

  const handleManualPriceClick = () => {
    const variationPrice = getPrice({
      parameters: activeVariation?.parameters,
    });
    togglePrice({ ...activeVariation, price: variationPrice?.result });
  };

  const handleDefaultClick = () => {
    setDefaultVariationId(activeVariationId);
  };

  const onDeleteParamValue = (paramId, paramValue) => {
    setParameters((state) => {
      const newState = { ...state };
      const configParams = newState[configurationId];
      if (!configParams) return state; // config not found, return original

      // Map through parameters and modify the correct one
      newState[configurationId] = configParams.map((param) => {
        if (param.id === paramId) {
          return {
            ...param,
            values: param.values.filter((v) => v !== paramValue),
          };
        }
        return param;
      });
      return newState;
    });
  };

  useEffect(() => {
    setInitialHotspots({ variations });
    // eslint-disable-next-line
  }, [variations ? JSON.stringify(variations) : ""]);

  return {
    configurationId,
    activeVariationId,
    parameters: parameters[configurationId] || [],
    constantsArray,
    onConstantsChange,
    hotspots: hotspots ? hotspots[activeVariationId] : [],
    hotspotsList: hotspots,
    onHotspotDrag,
    variations,
    filteredVariations,
    defaultVariationId,
    onFilterChange,
    onDuplicateHotspotCoords,
    tokens,
    onFormulaChange,
    formulaHistory,
    restoreFormula,
    getPrice,
    errorInFormula,
    handleDefaultClick,
    handleManualPriceClick,
    setInitialHotspots,
    moveHotspots,
    removeHotspotById,
    disableChange,
    onDeleteParamValue,
    showParametersDialog,
    setShowParametersDialog,
  };
};

export default useConfigurationPage;
